import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import { AnnounceBanner } from "@/features/announce/components/AnnounceBanner";
import { DesktopAnnouncementContent } from "@/features/announce/components/DesktopAnnouncementContent";
import { CatalogGrid } from "@/features/catalog/components/lists";
import { CatalogSortModal } from "@/features/catalog/components/modals/CatalogSortModal";
import { TagFilterModal } from "@/features/catalog/components/modals/TagFilterModal";
import { CatalogLoadMore } from "@/features/catalog/components/navigation/CatalogLoadMore";
import { CatalogPagination } from "@/features/catalog/components/navigation/CatalogPagination";
import { DesktopCatalogToolbar } from "@/features/catalog/components/navigation/DesktopCatalogToolbar";
import { MobileCatalogFooter } from "@/features/catalog/components/navigation/MobileCatalogFooter";
import { useInfiniteThreads, useThreads } from "@/features/catalog/hooks";
import {
  createCatalogContext,
  useCatalogStore,
} from "@/features/catalog/stores/catalogStore";
import { catalogContextBoardSlug } from "@/features/catalog/utils/catalogContext";
import {
  DesktopHistorySidebar,
  type DesktopHistorySidebarHandle,
} from "@/features/history/components/DesktopHistorySidebar";
import { DesktopSettingsModal } from "@/features/settings/components/desktop/DesktopSettingsModal";
import { useSettingsStore } from "@/features/settings/hooks";
import { DesktopThreadCreatePanel } from "@/features/thread/components/desktop/DesktopThreadCreatePanel";
import { ThreadCreateModal } from "@/features/thread/components/modals/ThreadCreateModal";
import { useOpenPanelOnFileDrag } from "@/features/thread/hooks/useOpenPanelOnFileDrag";
import { useThreadCreateModalStore } from "@/features/thread/stores/threadCreateModalStore";
import { useIsDesktop } from "@/shared/hooks";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useManualRefresh } from "@/shared/hooks/useManualRefresh";
import { hasLinkModifierKey } from "@/shared/lib/keyboardAccessibility";
import { BmgBanner } from "@/shared/ui/ad";
import { DmmWidget } from "@/shared/ui/ad/DmmWidget";
import { PullRefresh, RefreshStatus } from "@/shared/ui/feedback";

export const CatalogPage: React.FunctionComponent = () => {
  useDocumentTitle(BOARD_DISPLAY_NAME);
  const updateLastCatalogIds = useCatalogStore(
    (state) => state.updateLastCatalogIds,
  );
  const currentSort = useCatalogStore((state) => state.currentSort);
  const selectSortMethod = useCatalogStore((state) => state.selectSortMethod);
  const page = useCatalogStore((state) => state.page);
  const setPage = useCatalogStore((state) => state.setPage);
  const autoReload = useCatalogStore((state) => state.autoReload);
  const setAutoReload = useCatalogStore((state) => state.setAutoReload);
  const isOpen = useThreadCreateModalStore((s) => s.isOpen);
  const open = useThreadCreateModalStore((s) => s.open);
  const close = useThreadCreateModalStore((s) => s.close);
  const openThreadCreateOnFileDrag = useOpenPanelOnFileDrag(isOpen, open);
  const desktopThreadCreateButtonRef = useRef<HTMLButtonElement>(null);
  const desktopHistorySidebarRef = useRef<DesktopHistorySidebarHandle>(null);
  const isDesktop = useIsDesktop();
  const showDmmWidget = useSettingsStore((state) => !state.privacyMode);
  const threadsQuery = useThreads(undefined, { enabled: isDesktop });
  const infiniteThreadsQuery = useInfiniteThreads({ enabled: !isDesktop });
  const activeQuery = isDesktop ? threadsQuery : infiniteThreadsQuery;
  const { data, isFetching } = activeQuery;
  const refetch = useCallback(
    () => (isDesktop ? threadsQuery.refetch() : infiniteThreadsQuery.refetch()),
    [infiniteThreadsQuery.refetch, isDesktop, threadsQuery.refetch],
  );
  const latestThreadsRef = useRef(data?.threads);
  useEffect(() => {
    latestThreadsRef.current = data?.threads;
  }, [data?.threads]);
  const wheelReloadEnabled = useSettingsStore(
    (state) => state.wheelReloadEnabled,
  );
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);
  const navigate = useNavigate();
  // isDesktopがtrue→false→trueと往復した場合、isSettingsOpenの値が保持
  // されたままSP版に切り替わり、再度PC版に戻った瞬間に設定モーダルが
  // 意図せず再表示されるのを防ぐ。レンダー中に状態を調整するReactの
  // 推奨パターンを使う(https://react.dev/learn/you-might-not-need-an-effect)
  const [prevIsDesktop, setPrevIsDesktop] = useState(isDesktop);
  if (isDesktop !== prevIsDesktop) {
    setPrevIsDesktop(isDesktop);
    if (isDesktop) {
      setIsSortOpen(false);
      setIsTagFilterOpen(false);
    } else {
      setIsSettingsOpen(false);
    }
  }

  // Issue #208 はPCの手動更新だけを統一するため、SPのPullRefreshは
  // 従来どおり自動取得中の再実行を抑止する既存経路を維持する。
  const onRefresh = useCallback(async () => {
    if (isFetching) return;
    await refetch();
  }, [isFetching, refetch]);
  const { isManualRefreshing, refreshManually } = useManualRefresh(
    refetch,
    isFetching,
  );

  const closeDesktopThreadCreatePanel = useCallback((): void => {
    close();
    window.requestAnimationFrame(() => {
      desktopThreadCreateButtonRef.current?.focus();
    });
  }, [close]);

  // contextが切り替わる直前に、そのcontextで最後に取得した一覧を保存する。
  useEffect(() => {
    const context = createCatalogContext(
      catalogContextBoardSlug(),
      isDesktop ? page : 1,
      currentSort,
    );
    return (): void => {
      const threads = latestThreadsRef.current;
      if (threads) {
        updateLastCatalogIds(
          context,
          threads.map((thread) => thread.id),
        );
      }
    };
  }, [currentSort, isDesktop, page, updateLastCatalogIds]);

  // useThreadCreateModalStore はグローバルな zustand なので、ページ離脱時に
  // close() しないと再訪時に modal が開きっぱなしになる
  useEffect(() => {
    return (): void => {
      close();
    };
  }, [close]);

  if (isDesktop) {
    return (
      <section
        aria-labelledby="catalog-page-title"
        className="desktop-catalog-page"
      >
        <DesktopCatalogToolbar
          tagFilterThreads={data?.threads ?? []}
          currentSort={currentSort}
          selectSortMethod={selectSortMethod}
          autoReload={autoReload}
          setAutoReload={setAutoReload}
          isFetching={isFetching}
          onRefresh={(): void => void refreshManually()}
          onToggleSidebar={(): void =>
            desktopHistorySidebarRef.current?.toggle()
          }
          onScrollToTop={(): void => window.scrollTo({ top: 0 })}
          onScrollToBottom={(): void =>
            window.scrollTo({ top: document.documentElement.scrollHeight })
          }
          onSettingsClick={(
            event: React.MouseEvent<HTMLAnchorElement>,
          ): void => {
            // 修飾キー付きクリックでは従来どおり設定ページを別タブで開ける。
            if (hasLinkModifierKey(event)) return;
            event.preventDefault();
            setIsSettingsOpen(true);
          }}
          onOpenThreadCreate={open}
        />
        <RefreshStatus isRefreshing={isManualRefreshing} />
        <h1 id="catalog-page-title" className="desktop-mode-title">
          カタログ
        </h1>
        <DesktopAnnouncementContent>
          <section
            className="desktop-catalog-banner"
            aria-label="お知らせと広告"
          >
            <AnnounceBanner />
            <BmgBanner reserveEmptySpace priority />
          </section>
          <CatalogGrid
            query={threadsQuery}
            onDesktopRefresh={refreshManually}
            wheelReloadEnabled={wheelReloadEnabled}
          />
          {data && (
            <CatalogPagination
              page={page}
              hasNextPage={data.pagination.hasNextPage}
              isFetching={isFetching}
              onChange={setPage}
            />
          )}
          {data && showDmmWidget && <DmmWidget />}
        </DesktopAnnouncementContent>
        <DesktopHistorySidebar
          ref={desktopHistorySidebarRef}
          showClosedHandle={false}
        />
        <DesktopThreadCreatePanel
          isOpen={isOpen}
          onClose={closeDesktopThreadCreatePanel}
        />
        <DesktopSettingsModal
          isOpen={isSettingsOpen}
          onClose={(): void => setIsSettingsOpen(false)}
        />
        <button
          ref={desktopThreadCreateButtonRef}
          className="desktop-catalog-fab"
          type="button"
          aria-expanded={isOpen}
          onClick={isOpen ? closeDesktopThreadCreatePanel : open}
          onDragEnter={openThreadCreateOnFileDrag}
        >
          {isOpen ? <FiX aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
          {isOpen ? "閉じる" : "新規スレ"}
        </button>
      </section>
    );
  }

  return (
    <>
      <section aria-labelledby="catalog-page-title" className="pb-20">
        <h1 id="catalog-page-title" className="sr-only">
          カタログ
        </h1>
        <AnnounceBanner />
        <BmgBanner reserveEmptySpace priority />
        <PullRefresh onRefresh={onRefresh}>
          <CatalogGrid
            query={infiniteThreadsQuery}
            onDesktopRefresh={(): void => undefined}
            wheelReloadEnabled={wheelReloadEnabled}
          />
          {data && (
            <CatalogLoadMore
              errorMessage={infiniteThreadsQuery.nextPageError}
              hasNextPage={infiniteThreadsQuery.hasNextPage}
              isFetching={isFetching}
              onLoadMore={(): void => {
                void infiniteThreadsQuery.fetchNextPage();
              }}
            />
          )}
        </PullRefresh>
      </section>
      <MobileCatalogFooter
        isRefreshing={isFetching}
        onOpenSort={(): void => setIsSortOpen(true)}
        onOpenTagFilter={(): void => setIsTagFilterOpen(true)}
        onRefresh={(): void => void onRefresh()}
        primaryAction={{
          icon: FiPlus,
          label: "新規スレッド",
          onClick: open,
        }}
      />
      {isSortOpen && (
        <CatalogSortModal
          isOpen
          currentSort={currentSort}
          selectSortMethod={selectSortMethod}
          onShowCatalog={(): void => undefined}
          onShowList={(): void => {
            void navigate({ to: "/board" });
          }}
          onClose={(): void => setIsSortOpen(false)}
        />
      )}
      <TagFilterModal
        isOpen={isTagFilterOpen}
        threads={data?.threads ?? []}
        onClose={(): void => setIsTagFilterOpen(false)}
      />
      <ThreadCreateModal isOpen={isOpen} onClose={close} />
    </>
  );
};
